import { useAtomValue } from "jotai";
import { Link } from "react-router-dom";
import { ROUTES } from "@routing/routes";
import type { LocationResponse } from "@models/location";
import { hasNoLocationMembershipAtom } from "@/store/shipToAtoms";
import {
    SHIPPING_LOCATION_GAP_COPY,
    type ShippingLocationGap,
} from "./_helpers/shippingLocationCopy";

interface ShippingCardProps {
    location?: LocationResponse;
    /** When provided, renders a "Change" button. Omit for a read-only card. */
    onChange?: () => void;
    /** With no location: why there is none (#403, PR 367 review finding 4). */
    locationGap?: ShippingLocationGap;
    /** Re-reads the locations; rendered as Retry when the read failed. */
    onRetryLocations?: () => void;
}

// Where the shopper can see, add or fix their locations.
const AccountLocationsLink = ({ lead }: { lead: string }) => (
    <p>
        {lead}{" "}
        <Link to={ROUTES.ACCOUNT_LOCATIONS} className="link link-primary">
            Account › Locations
        </Link>
        .
    </p>
);

export const ShippingCard = ({
    location,
    onChange,
    locationGap,
    onRetryLocations,
}: ShippingCardProps) => {
    // #417. A member seated on no location has no location to select, and
    // "Change" would open onto an empty picker — so for them the card says why
    // and offers nothing to click. The page's note names the way out.
    const hasNoLocationMembership = useAtomValue(hasNoLocationMembershipAtom);
    const isNoLocation = hasNoLocationMembership && !location;
    // #417 wins over every other reason for having no destination.
    const gap = location || isNoLocation ? undefined : locationGap;

    return (
        <div className="bg-base-100 rounded-lg p-4 md:p-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral/60">
                    Shipping to
                </p>
                {location ? (
                    <>
                        <p className="font-bold text-neutral mt-1">
                            {location.name}
                        </p>
                        {location.streetAddress && (
                            <p className="text-sm text-neutral/70">
                                {location.streetAddress}
                            </p>
                        )}
                        {(location.city ||
                            location.state ||
                            location.zipCode) && (
                            <p className="text-sm text-neutral/70">
                                {[location.city, location.state]
                                    .filter(Boolean)
                                    .join(", ")}
                                {location.zipCode
                                    ? ` ${location.zipCode}`
                                    : ""}
                            </p>
                        )}
                    </>
                ) : gap === "noneEligible" ? (
                    <div className="text-sm text-neutral/70 mt-1">
                        <p>{SHIPPING_LOCATION_GAP_COPY.noneEligible}.</p>
                        <AccountLocationsLink lead="You can review them in" />
                    </div>
                ) : gap === "noLocations" ? (
                    <div className="text-sm text-neutral/70 mt-1">
                        <p>{SHIPPING_LOCATION_GAP_COPY.noLocations}.</p>
                        <AccountLocationsLink lead="You can add one in" />
                    </div>
                ) : gap === "loadFailed" ? (
                    <p className="text-sm text-error mt-1">
                        {SHIPPING_LOCATION_GAP_COPY.loadFailed}.
                    </p>
                ) : gap === "loading" ? (
                    <p className="text-sm text-neutral/70 mt-1">
                        {SHIPPING_LOCATION_GAP_COPY.loading}
                    </p>
                ) : (
                    <p className="text-sm text-neutral/70 mt-1">
                        {isNoLocation
                            ? "No shipping location assigned to you yet."
                            : "No shipping location selected."}
                    </p>
                )}
            </div>
            {gap === "loadFailed" && onRetryLocations ? (
                <button
                    type="button"
                    onClick={onRetryLocations}
                    className="btn btn-sm btn-outline text-neutral border-neutral shrink-0"
                >
                    Retry
                </button>
            ) : (
                onChange &&
                !isNoLocation && (
                    <button
                        type="button"
                        onClick={onChange}
                        className="btn btn-sm btn-outline text-neutral border-neutral shrink-0"
                    >
                        Change
                    </button>
                )
            )}
        </div>
    );
};
